import { t } from 'i18next';
import { useFormContext } from 'react-hook-form';

import {
  CheckBoxOutlineBlank,
  SelectCheckBox,
  VFlex,
  VInput,
  VStack,
  cn,
} from '@/package';
import type { ObjectClass } from '@/type';

import type { TaskCreateFormType } from './type';

export const TaskClassConfidence = ({
  readOnly,
  extendClassList,
}: {
  readOnly: boolean;
  extendClassList: ObjectClass[];
}) => {
  const { watch, register, setValue, getValues } =
    useFormContext<TaskCreateFormType>();
  const selectedExtendClass = watch('extendClass') ?? [];
  const classConfidence = watch('classConfidence') ?? [];

  const isValid = (value: Numberlish, min: number, max: number) => {
    if (isNaN(+value)) return false;
    if (+value < min) return false;
    if (+value > max) return false;
    return true;
  };

  const validateExtendClass = (value?: TaskCreateFormType['extendClass']) => {
    const currentExtendClass = value ?? getValues('extendClass') ?? [];
    return Boolean(currentExtendClass?.length && currentExtendClass.length > 0);
  };

  const validateClassConfidence = (
    value?: TaskCreateFormType['classConfidence'],
  ) => {
    const currentExtendClass = getValues('extendClass') ?? [];
    const currentClassConfidence = value ?? getValues('classConfidence') ?? [];
    if (!currentExtendClass?.length) return true;
    return currentExtendClass.every(extendClass => {
      const confidence = currentClassConfidence?.find(
        c => c.id === extendClass.id,
      );
      if (!confidence) return false;
      return isValid(
        confidence.confidence,
        extendClassList.find(c => c.id === extendClass.id)?.confidenceMin ?? 0,
        extendClassList.find(c => c.id === extendClass.id)?.confidenceMax ?? 1,
      );
    });
  };

  register('extendClass', {
    validate: validateExtendClass,
  });
  register('classConfidence', {
    validate: validateClassConfidence,
  });

  return (
    <VStack className="flex-1">
      <VFlex
        items="center"
        className="border-y border-gray-300 text-body-md text-gray-500"
      >
        <VFlex
          justify="center"
          className={cn(
            'box-content h-5 w-14 p-2 text-center',
            readOnly ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
          role="checkbox"
          onClick={() => {
            if (readOnly) return;

            const selectedLength = (selectedExtendClass ?? []).length;

            setValue(
              'extendClass',
              selectedLength === extendClassList?.length ? [] : extendClassList,
              { shouldValidate: true },
            );
          }}
        >
          <SelectCheckBox className="size-5" />
        </VFlex>
        <div className="box-content h-5 w-[187px] p-2">Target object</div>
        <div className="box-content h-5 flex-1 p-2">Confidence</div>
      </VFlex>

      {extendClassList.map(item => {
        const selected = selectedExtendClass;
        const isSelected = selected.some(
          extendClass => extendClass.id === item.id,
        );

        return (
          <VFlex
            items="center"
            className={cn(
              'border-y border-gray-300 text-body-md text-gray-500',
              {
                'bg-Primary-Navy-Active': isSelected,
              },
            )}
            key={item.id}
          >
            <VFlex
              justify="center"
              items="center"
              className={cn(
                'box-content h-5 w-14 p-2 text-center',
                readOnly ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
              role="checkbox"
              onClick={() => {
                if (readOnly) return;

                const nextExtendClass = isSelected
                  ? selected.filter(extendClass => extendClass.id !== item.id)
                  : [...selected, item];

                setValue('extendClass', nextExtendClass, {
                  shouldValidate: true,
                });
              }}
            >
              {isSelected ? (
                <SelectCheckBox className="size-5" />
              ) : (
                <CheckBoxOutlineBlank className="size-5" />
              )}
            </VFlex>
            <VFlex
              className="box-content h-7 w-[187px] px-2 py-1 font-semibold text-black capitalize"
              items="center"
            >
              {item.alias || item.name}
            </VFlex>
            <VFlex className="box-content h-7 flex-1 px-2 py-1" items="center">
              <VInput
                className="flex-1"
                label={t('preAnnotation.confidence.vaccinatory')}
                type="number"
                max={item.confidenceMax}
                min={item.confidenceMin}
                step={0.001}
                required
                readOnly={readOnly}
                size="sm"
                orientation="horizontal"
                valid={isValid(
                  classConfidence?.find(confidence => confidence.id === item.id)
                    ?.confidence ?? -1,
                  item.confidenceMin ?? 0,
                  item.confidenceMax ?? 1,
                )}
                value={
                  readOnly
                    ? Number(
                        classConfidence?.find(
                          confidence => confidence.id === item.id,
                        )?.confidence ?? 0.5,
                      ).toFixed(3)
                    : classConfidence?.find(
                        confidence => confidence.id === item.id,
                      )?.confidence
                }
                onBlur={e => {
                  e.target.value = e.target.valueAsNumber.toFixed(3);
                }}
                onChange={e => {
                  const nextClassConfidence = classConfidence.filter(
                    confidence => confidence.id !== item.id,
                  );

                  setValue(
                    'classConfidence',
                    [
                      ...nextClassConfidence,
                      {
                        id: item.id,
                        confidence: e.target.valueAsNumber,
                        classType: item.classType,
                      },
                    ],
                    { shouldValidate: true },
                  );
                }}
              />
            </VFlex>
          </VFlex>
        );
      })}
    </VStack>
  );
};
